export interface LiveOpsOverride {
  id: string
  activeAt?: string
  expiresAt?: string
  values: Record<string, unknown>
}

export interface LiveOpsVariant {
  id: string
  weight: number
  values: Record<string, unknown>
}

export interface LiveOpsExperiment {
  id: string
  salt?: string
  activeAt?: string
  expiresAt?: string
  variants: LiveOpsVariant[]
  /** Mock-mode only: short-circuits hashing. Rejected by push-time validators. */
  forceVariant?: string
}

export interface ExperimentAssignment {
  experimentId: string
  variantId: string
  /** Configured weights, forwarded into the exposure event so the warehouse can
   *  compare the observed split against the intended one (sample-ratio check). */
  variantWeight: number
  totalWeight: number
}

/** assignVariant's return: the winning variant plus the weight sum it won under. */
export interface AssignedVariant {
  variant: LiveOpsVariant
  totalWeight: number
}

export interface LiveOpsSection {
  values?: Record<string, unknown>
  overrides?: LiveOpsOverride[]
  experiments?: LiveOpsExperiment[]
}

export interface ResolvedLiveOps {
  values: Record<string, unknown>
  activeOverrideIds: string[]
  assignments: ExperimentAssignment[]
  /** Epoch-ms of the next window boundary that changes the active set, or null. */
  nextChangeAt: number | null
}

interface TimeWindow {
  activeAt?: string
  expiresAt?: string
}

function windowContains(window: TimeWindow, nowMs: number): boolean {
  if (window.activeAt !== undefined) {
    const start = Date.parse(window.activeAt)
    if (Number.isNaN(start) || nowMs < start) return false
  }
  if (window.expiresAt !== undefined) {
    const end = Date.parse(window.expiresAt)
    if (Number.isNaN(end) || nowMs >= end) return false
  }
  return true
}

/** Degrade-not-throw: a non-array section field (hand-written/legacy data) must
 *  resolve to "empty", never make `for...of` / spread throw TypeError. */
function asArray<T>(value: T[] | undefined): T[] {
  return Array.isArray(value) ? value : []
}

function hasUnparseableDate(window: TimeWindow): boolean {
  return (
    (window.activeAt !== undefined && Number.isNaN(Date.parse(window.activeAt))) ||
    (window.expiresAt !== undefined && Number.isNaN(Date.parse(window.expiresAt)))
  )
}

/**
 * Earliest future boundary (activeAt or expiresAt strictly after now) across all
 * overrides and experiments with parseable dates, or null. Skipped/bad-date
 * entries contribute none.
 */
function computeNextChangeAt(
  section: LiveOpsSection | undefined,
  nowMs: number,
): number | null {
  let next: number | null = null
  const windows: TimeWindow[] = [
    ...asArray(section?.overrides),
    ...asArray(section?.experiments),
  ]
  for (const window of windows) {
    for (const raw of [window.activeAt, window.expiresAt]) {
      if (raw === undefined) continue
      const t = Date.parse(raw)
      if (Number.isNaN(t) || t <= nowMs) continue
      if (next === null || t < next) next = t
    }
  }
  return next
}

/** FNV-1a 32-bit. Stable, portable; do not change without re-shuffling every live experiment. */
export function fnv1a32(input: string): number {
  let hash = 0x811c9dc5
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193) >>> 0
  }
  return hash >>> 0
}

export const BUCKET_COUNT = 10000

/** Double-hash into [0, BUCKET_COUNT). The double pass is load-bearing: bit 0 of a
 *  single fnv1a32 is a parity function of the input chars (odd-char XOR flips it,
 *  odd-prime multiply preserves it), so `hash % 2` gave every 50/50 experiment the
 *  same global player split (verified: 0%/100% cross-experiment agreement). Hashing
 *  the first hash's decimal string breaks that structure (cf. GrowthBook hashVersion 2).
 *  Do not change without re-shuffling every live experiment. */
export function assignmentBucket(seed: string, unitId: string): number {
  return fnv1a32(String(fnv1a32(`${seed}:${unitId}`))) % BUCKET_COUNT
}

/** Pure deterministic assignment; null on malformed input (degrade-not-throw).
 *  Variants own contiguous bucket ranges in array order, boundaries scaled to
 *  BUCKET_COUNT, so a constant-total weight edit moves only the boundaries,
 *  which is what makes the documented ramp recipe safe. */
export function assignVariant(
  experiment: LiveOpsExperiment,
  unitId: string,
): AssignedVariant | null {
  const variants = Array.isArray(experiment.variants) ? experiment.variants : []
  if (experiment.forceVariant !== undefined) {
    const forced = variants.find(v => v.id === experiment.forceVariant)
    // totalWeight 0 marks a forced (mock-only) assignment; mock exposures never ship.
    return forced ? { variant: forced, totalWeight: 0 } : null
  }
  // Parity with push-time validation: an experiment needs >= 2 variants.
  if (variants.length < 2) return null
  let total = 0
  for (const v of variants) {
    if (typeof v.weight !== 'number' || !Number.isInteger(v.weight) || v.weight < 1) {
      return null
    }
    total += v.weight
  }
  // total > BUCKET_COUNT would give sub-1/10000 variants an empty bucket range and
  // silently starve them; validators reject it at push, resolution degrades here.
  if (total < 1 || total > BUCKET_COUNT) return null
  const bucket = assignmentBucket(experiment.salt ?? experiment.id, unitId)
  let cumulative = 0
  for (const v of variants) {
    cumulative += v.weight
    // floor(BUCKET_COUNT * total / total) === BUCKET_COUNT > any bucket, so the
    // last variant always catches.
    if (bucket < Math.floor((BUCKET_COUNT * cumulative) / total)) {
      return { variant: v, totalWeight: total }
    }
  }
  return null
}

/**
 * Resolve a LiveOps `client` section against a caller-supplied clock. Pure: never
 * reads an ambient clock (`nowMs` is server-anchored in the SDK real paths,
 * `Date.now()` in Mock, UTC now in the CLI preview). Purity is load-bearing — the
 * MP authority resolves once at match start with a frozen timestamp.
 */
export function resolveLiveOpsSection(
  section: LiveOpsSection | undefined,
  nowMs: number,
  unitId?: string,
): ResolvedLiveOps {
  const values: Record<string, unknown> = { ...(section?.values ?? {}) }
  const activeOverrideIds: string[] = []
  for (const override of asArray(section?.overrides)) {
    // Unparseable dates were rejected at upload for new snapshots; old/hand-written
    // data must degrade to "override inactive", never to a throw.
    if (hasUnparseableDate(override) || !windowContains(override, nowMs)) continue
    Object.assign(values, override.values)
    activeOverrideIds.push(override.id)
  }
  const assignments: ExperimentAssignment[] = []
  if (unitId !== undefined && unitId !== '') {
    // Experiment values land after overrides, so an assigned variant wins.
    for (const experiment of asArray(section?.experiments)) {
      if (hasUnparseableDate(experiment) || !windowContains(experiment, nowMs)) continue
      const assigned = assignVariant(experiment, unitId)
      if (assigned === null || typeof experiment.id !== 'string' || experiment.id === '') continue
      Object.assign(values, assigned.variant.values ?? {})
      assignments.push({
        experimentId: experiment.id,
        variantId: assigned.variant.id,
        variantWeight: assigned.variant.weight,
        totalWeight: assigned.totalWeight,
      })
    }
  }
  return {
    values,
    activeOverrideIds,
    assignments,
    nextChangeAt: computeNextChangeAt(section, nowMs),
  }
}
